//@flow
import React, {useEffect, useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {MenuItem, makeStyles, Typography} from '@material-ui/core';
import CustomSelect from '../../../../../../../../elements/CustomSelect/CustomSelect';
import {TRIGGER_FIELDS} from '../../../const';
import {FILTER_TYPES} from '../../../../../utils/triggerConfig';
import {
  FILTER_SET_VALUE_OPTIONS,
  setFilterFieldUpdates,
} from '../../../../../utils/filterFields';
import {useMultichatFunnels} from '../../../../../../funnels/utils';
import {getFormError} from '../../../../../utils/formErrors';
import {colors} from '../../../../../../../../assets/styles/colors';
import type {CrmFunnelFilter as CrmFunnelFilterValues} from '../../../../../../../../entities';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  label: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  fieldError: {
    fontSize: '.8125rem',
    color: colors.danger,
  },
}));

type Props = {
  filterType: typeof FILTER_TYPES.CRM_FUNNEL,
};

export default function CrmFunnelFilter({filterType}: Props) {
  const classes = useStyles();
  const {register, watch, setValue, getValues, errors} = useFormContext();
  const {funnels} = useMultichatFunnels();
  const safeFunnels = Array.isArray(funnels) ? funnels : [];

  const fieldPrefix = `${TRIGGER_FIELDS.filters}.${filterType}`;

  useEffect(() => {
    register(`${fieldPrefix}.funnelId`);
    register(`${fieldPrefix}.funnelLaneId`);
  }, [register, fieldPrefix]);

  const allFilters = watch(TRIGGER_FIELDS.filters) || {};
  const filter: CrmFunnelFilterValues = allFilters[filterType] || {
    funnelId: '',
    funnelLaneId: '',
  };

  const funnelError = getFormError(errors, `${fieldPrefix}.funnelId`);
  const laneError = getFormError(errors, `${fieldPrefix}.funnelLaneId`);

  const selectedFunnel = useMemo(
    () => safeFunnels.find(funnel => funnel.id === filter.funnelId),
    [safeFunnels, filter.funnelId]
  );

  const lanes = selectedFunnel?.lanes || [];

  return (
    <div className={classes.root}>
      <div className={classes.field}>
        <Typography className={classes.label}>Embudo</Typography>
        <CustomSelect
          placeholder="Seleccionar embudo"
          value={filter.funnelId || ''}
          onChange={event => {
            setFilterFieldUpdates(
              getValues,
              setValue,
              filterType,
              {funnelId: event.target.value, funnelLaneId: ''},
              FILTER_SET_VALUE_OPTIONS
            );
          }}
          error={!!funnelError}
        >
          {safeFunnels.map(funnel => (
            <MenuItem key={funnel.id} value={funnel.id}>
              {funnel.name}
            </MenuItem>
          ))}
        </CustomSelect>
        {funnelError && (
          <Typography className={classes.fieldError}>{funnelError}</Typography>
        )}
      </div>

      <div className={classes.field}>
        <Typography className={classes.label}>Etapa</Typography>
        <CustomSelect
          placeholder="Seleccionar etapa"
          value={filter.funnelLaneId || ''}
          disabled={!filter.funnelId}
          onChange={event => {
            setFilterFieldUpdates(
              getValues,
              setValue,
              filterType,
              {funnelLaneId: event.target.value},
              FILTER_SET_VALUE_OPTIONS
            );
          }}
          error={!!laneError}
        >
          {lanes.map(lane => (
            <MenuItem key={lane.id} value={lane.id}>
              {lane.stage?.label || `Etapa ${lane.number}`}
            </MenuItem>
          ))}
        </CustomSelect>
        {laneError && (
          <Typography className={classes.fieldError}>{laneError}</Typography>
        )}
      </div>
    </div>
  );
}
